/**
 * chapterTitle.service.ts
 *
 * Neutral shared service for resolving chapter titles defensively.
 * Safe for use in both frontend UI components and backend/export service layers.
 */

const isGenericTitle = (title: string): boolean => {
  return /^chapter\s+\d+$/i.test(title.trim());
};

export const resolveChapterTitle = (
  chapter?: { title?: string; chapterTitle?: string; name?: string; heading?: string } | null,
  roadmapChapter?: { title?: string; chapterTitle?: string; name?: string; heading?: string } | null,
  chapterNumber?: number | string
): string => {
  const num = chapterNumber || chapter?.chapterNumber || (chapter as any)?.id || roadmapChapter?.chapterNumber || '';

  // Helper to extract non-empty, non-generic value from an object using aliases
  const getVal = (obj: any, allowGeneric = true): string | null => {
    if (!obj) return null;
    const v = obj.title || obj.chapterTitle || obj.name || obj.heading;
    if (typeof v === 'string' && v.trim()) {
      const trimmed = v.trim();
      if (!allowGeneric && isGenericTitle(trimmed)) {
        return null;
      }
      return trimmed;
    }
    return null;
  };

  // 1. Prefer approved roadmap title aliases first (always non-empty)
  const roadmapVal = getVal(roadmapChapter, true);
  if (roadmapVal) {
    return roadmapVal;
  }

  // 2. Prefer chapter artifact title aliases second (excluding generic "Chapter X" fallbacks)
  const chapterVal = getVal(chapter, false);
  if (chapterVal) {
    return chapterVal;
  }

  // 3. Fallback to generic "Chapter {Number}"
  return `Chapter ${num}`;
};
